import React from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';

import moment from 'moment';
import BarChart from './BarChart';
import UpdateTrackingInfo from '../OrdersV2/UpdateTrackingInfo';

import { Theme } from '../Styles';
import { ArrowDownIcon, CalendarIcon, DSODropdownIcon, ScheduleIcon, ProcessingIcon, AddIcon, GoToIcon } from './Styles/Icons';
import { SummaryCardOuter, SummaryCardInner, Filter, PopoverBody, PopoverItem, Popover, PopoverTitle, Search, RootContainer,
    TitleSection, TitleRow, Title, FlexColumn, Container, CircularProgress, DateRangePicker, LocalizationProvider, FlexRow, SubTitle,
    StatValue, StatText, FilterButton, ButtonGroup, Button } from './Styles/Home';

import { getStats, getSalesSummary, getDSOList } from '../../actions/statsActions';
import { changeTile } from '../../actions/changeTile';
import { updateError } from '../../actions/commonActions';
import { SPJ_ACTIONS } from '../../commons/utils';
import { UploadOrdersFileActions } from '../OrdersV2/Utils';
import { setUploadOrdersFileAction, setUploadOrdersFileBatch, fetchUploadOrdersFileBatch } from '../../actions/ordersActions';

const PopoverIds = {
	FILTER_BY_DSO: 'filter-by-dso',
	FILTER_BY_PERIOD: 'filter-by-period',
};
const FilterByPeriodOptions = [
	{ name: 'Daily', value: 'day', disabled: false },
	{ name: 'Weekly', value: 'week', disabled: false },
	{ name: 'Monthly', value: 'month', disabled: false },
];

class Home extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			statsFetching: true,
			stats: null,
			dateRange: [new Date(), new Date()],
			dateUnit: 'day',
			lower_limit: 1,
			upper_limit: 29,
			supplier: '',
			dateRangePickerOpen: false,
			dsoPopoverAnchorElement: null,
			dsoOptions: [],
			dsoPopoverItem: '',
			dsoOptionsFetching: false,
			periodPopoverAnchorElement: null,
			periodPopoverItem: 'day',
			fetchingUploadOrdersFileBatch: false,
			updateTrackingInfoOpen: false
		};
	}

	async componentDidMount() {
		if (this.props.history.action === 'POP') {
			this.props.changeTile('HOME');
		}

		if (this.props.tile !== 'HOME') {
			this.props.changeTile('HOME');
		}

		let currDate = new Date();
		let prevDate = new Date();
		prevDate.setDate(prevDate.getDate() - 29);
		this.setState({
			dateRange: [prevDate, currDate],
		});
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.props.stats !== this.state.stats) {
			this.setState({ stats: this.props.stats });
		}

		if (this.props.supplier !== this.state.supplier) {
			this.setState(
				{
					supplier: this.props.supplier,
					dsoPopoverItem: '',
					dsoOptions: [],
				},
				async () => {
					this.fetchDSOOptions();
					this.fetchStats();
					this.fetchSummary();
					await this.fetchUploadOrdersFileBatch();
				}
			);
		}

		if (prevState.dsoPopoverItem !== this.state.dsoPopoverItem) {
			this.fetchStats();
			this.fetchSummary();
		}

		if (prevState.periodPopoverItem !== this.state.periodPopoverItem || prevState.dateRange[0] !== this.state.dateRange[0] || prevState.dateRange[1] !== this.state.dateRange[1]) {
			this.fetchSummary();
		}
	}

	handlePopoverClick = async (popoverId, event) => {
		switch (popoverId) {
			case PopoverIds.FILTER_BY_DSO:
				this.setState({
					dsoPopoverAnchorElement: event.currentTarget,
				});
				break;
			case PopoverIds.FILTER_BY_PERIOD:
				this.setState({
					periodPopoverAnchorElement: event.currentTarget,
				});
				break;
			default:
				break;
		}
	};

	handlePopoverSelect = async (popoverId, choice) => {
		switch (popoverId) {
			case PopoverIds.FILTER_BY_DSO:
				this.setState({
					dsoPopoverItem: choice.value,
				});
				this.handlePopoverClose(popoverId);
				break;
			case PopoverIds.FILTER_BY_PERIOD:
				this.handlePeriodChange(choice.value);
				this.handlePopoverClose(popoverId);
				break;
			default:
				this.handlePopoverClose(popoverId);
				break;
		}
	};

	handlePopoverClose = async (popoverId) => {
		switch (popoverId) {
			case PopoverIds.FILTER_BY_DSO:
				this.setState({
					dsoPopoverAnchorElement: null,
					dsoSearchText: '',
				});
				break;
			case PopoverIds.FILTER_BY_PERIOD:
				this.setState({
					periodPopoverAnchorElement: null,
				});
				break;
			default:
				break;
		}
	};

	handlePeriodChange = async (period) => {
		let { dateRange } = this.state;
		let days = (dateRange[1] - dateRange[0]) / (1000 * 60 * 60 * 24);
		let weeks = days / 7;
		let months = days / 30;

		if (period === 'day' && days > 30) {
			this.props.updateError(true, 'Infeasible period, Please select a different period');
		} else if (period === 'week' && weeks > 30) {
			this.props.updateError(true, 'Infeasible period, Please select a different period');
		} else {
			this.setState({ periodPopoverItem: period });
		}
	};

	handleDateSelectOpen = () => {
		this.setState({
			dateRangePickerOpen: true,
		});
	};

	handleDateChange = (dateRange) => {
		if (dateRange[1] != null) {
			this.setState({
				dateRange: dateRange,
			});
		}
	};

	handleDateSelectClose = () => {
		this.setState({
			dateRangePickerOpen: false,
		});

		let { dateRange } = this.state;
		let days = (dateRange[1] - dateRange[0]) / (1000 * 60 * 60 * 24);
		let weeks = days / 7;
		let months = days / 30;

		if (days <= 30) {
			this.setState({ periodPopoverItem: 'day' });
		} else if (weeks <= 30) {
			this.setState({ periodPopoverItem: 'week' });
		} else {
			this.setState({ periodPopoverItem: 'month' });
		}
	};

	handleDateUnitChange = (event) => {
		let unit = event.target.value;
		let days = (this.state.dateRange[1] - this.state.dateRange[0]) / (1000 * 3600 * 24);
		let weeks = days / 7;
		let months = days / 12;

		if (unit === 'day' && days > this.state.upper_limit) {
			this.props.updateError(true, 'Infeasible Date Unit, Please Select a diffrent Date Unit');
			return;
		} else if (unit === 'week' && (weeks > this.state.upper_limit || weeks < this.state.lower_limit)) {
			this.props.updateError(true, 'Infeasible Date Unit, Please Select a diffrent Date Unit');
			return;
		} else if (unit === 'month' && (months > this.state.upper_limit || months < this.state.lower_limit)) {
			this.props.updateError(true, 'Infeasible Date Unit, Please Select a different Date Unit');
			return;
		} else {
			this.props
				.getSalesSummary(this.state.dsoPopoverItem || '', unit, moment(this.state.dateRange[0]).format('yyyy-MM-DD'), moment(this.state.dateRange[1]).format('yyyy-MM-DD'))
				.then((res) => {
					if (res) {
						this.setState({
							dateUnit: unit,
						});
					} else {
						this.props.updateError(true, 'Unable to fetch data');
					}
				});
		}
	};

	fetchDSOOptions = async () => {
		this.setState({
			dsoOptionsFetching: true,
		});

		let list = await this.props.getDSOList();
		list = list.map((dso) => ({
			name: dso.group_name,
			value: dso._id,
			disabled: false,
		}));

		this.setState({
			dsoOptionsFetching: false,
			dsoOptions: [{ name: 'All DSOs', value: '', disabled: false }, ...list],
		});
	};

	fetchStats = async () => {
		let { dsoPopoverItem } = this.state;
		this.setState({ statsFetching: true });

		await this.props.getStats(dsoPopoverItem);
		this.setState({ statsFetching: false });
	};

	fetchSummary = async () => {
		let { dsoPopoverItem, periodPopoverItem, dateRange } = this.state;
		this.setState({ summaryFetching: true });

		await this.props.getSalesSummary(dsoPopoverItem, periodPopoverItem, moment(dateRange[0]).format('yyyy-MM-DD'), moment(dateRange[1]).format('yyyy-MM-DD'));
		this.setState({ summaryFetching: false });
	};


	fetchUploadOrdersFileBatch = async () => {
		this.props.setUploadOrdersFileAction(false);
		this.props.setUploadOrdersFileBatch('');

		this.setState({ fetchingUploadOrdersFileBatch: true });
		await this.props.fetchUploadOrdersFileBatch();
		this.setState({ fetchingUploadOrdersFileBatch: false });
	};

	toggleUpdateTrackingInfoOpen = () => {
		this.setState({ updateTrackingInfoOpen: !this.state.updateTrackingInfoOpen });
	};

	showUploadOrdersFileDialog = () => {
		let { uploadOrdersFileType } = this.props;
		if (uploadOrdersFileType === SPJ_ACTIONS.SALES_ORDER_UPDATE) {
			this.toggleUpdateTrackingInfoOpen();
		}
	};

	render() {
		let { dsoPopoverAnchorElement, dsoPopoverItem, dsoSearchText, dsoOptionsFetching, dsoOptions, 
			statsFetching, stats, dateRange, dateRangePickerOpen, dateUnit, periodPopoverAnchorElement, 
			periodPopoverItem, updateTrackingInfoOpen } = this.state;
		let { graphData, disableOrdersV2, disableBulkUploadOrderTracking, showUploadOrdersFile } = this.props;

		let filterByDSOLabel = (dsoOptions.find((option) => option.value === dsoPopoverItem) || {}).name || '';
		let filterByPeriodLabel = (FilterByPeriodOptions.find((option) => option.value === periodPopoverItem) || {}).name || '';

		return (
			<RootContainer>
				<Helmet>
					<title>Seller Portal | Home</title>
				</Helmet>

				{updateTrackingInfoOpen ? (
					<UpdateTrackingInfo
						open={updateTrackingInfoOpen}
						dsoOptions={dsoOptions}
						toggle={this.toggleUpdateTrackingInfoOpen}
						onSuccess={this.fetchStats}
					/>
				) : null}

				<Popover
					id={PopoverIds.FILTER_BY_DSO}
					open={Boolean(dsoPopoverAnchorElement)}
					anchorEl={dsoPopoverAnchorElement}
					onClose={() => this.handlePopoverClose(PopoverIds.FILTER_BY_DSO)}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'right',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
				>
					<PopoverTitle>Select DSO</PopoverTitle>

					<PopoverItem>
						<Search
							searchText={dsoSearchText}
							placeholder={'Search DSO'}
							searchOnChange={true}
							onSearchHandler={(value) =>
								this.setState({
									dsoSearchText: value || '',
								})
							}
						/>
					</PopoverItem>

					<PopoverBody>
						{dsoOptionsFetching ? (
							<CircularProgress 
								showSpinner={true}
								size={40}
							/>
						) : (
							dsoOptions.map((item, index) => {
								return item.name.toUpperCase().includes((dsoSearchText || '').toUpperCase()) ? (
									<PopoverItem
										key={index}
										disabled={item.disabled || item.value === dsoPopoverItem}
										onClick={(e) => this.handlePopoverSelect(PopoverIds.FILTER_BY_DSO, item)}
									>
										{item.name}
									</PopoverItem>
								) : null;
							})
						)}
					</PopoverBody>
				</Popover>

				<Popover
					id={PopoverIds.FILTER_BY_PERIOD}
					open={Boolean(periodPopoverAnchorElement)}
					anchorEl={periodPopoverAnchorElement}
					onClose={() => this.handlePopoverClose(PopoverIds.FILTER_BY_PERIOD)}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'right',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
				>
					<PopoverTitle>Select Period</PopoverTitle>

					<PopoverBody>
						{FilterByPeriodOptions.map((item, index) => (
							<PopoverItem
								key={index}
								disabled={item.disabled || item.value === periodPopoverItem}
								onClick={(e) => this.handlePopoverSelect(PopoverIds.FILTER_BY_PERIOD, item)}
							>
								{item.name}
							</PopoverItem>
						))}
					</PopoverBody>
				</Popover>

				<TitleSection>
					<TitleRow style={{ flexWrap: 'wrap' }}>
						<FlexColumn style={{ flex: 3 }}>
							<FlexRow>
								<Title>Home</Title>
							</FlexRow>
						</FlexColumn>

						<FlexColumn style={{ flex: 9 }}>
							<FlexRow style={{ alignSelf: 'flex-end' }}>
								<Filter onClick={(e) => this.handlePopoverClick(PopoverIds.FILTER_BY_DSO, e)}>
									{filterByDSOLabel}
									<DSODropdownIcon />
								</Filter>
							</FlexRow>
						</FlexColumn>
					</TitleRow>
				</TitleSection>

				<Container style={{ margin: '0' }}>
					<FlexRow style={{ padding: '0 1.5rem', gap: '1rem' }}>
						<FlexColumn style={{ flex: 1 }}>
							<SummaryCardOuter height='100%'>
								<SummaryCardInner>
									<FlexRow style={{ alignItems: 'center', justifyContent: 'space-between' }}>
										<SubTitle>Orders</SubTitle>

										{!disableBulkUploadOrderTracking && stats && stats.orders && stats.orders.pending > 0 ? (
											showUploadOrdersFile ? (
												<Button
													secondary
													onClick={(e) => this.showUploadOrdersFileDialog()}
												>
													<ProcessingIcon />
													Show Upload Progress
												</Button>
											) : (
												<Button
													primary
													onClick={(e) => {
														this.props.setUploadOrdersFileAction(true, SPJ_ACTIONS.SALES_ORDER_UPDATE, UploadOrdersFileActions.UPLOAD);
														this.toggleUpdateTrackingInfoOpen()
													}}
												>
													<AddIcon />
													Update Order Status And Tracking Info
												</Button>
											)
										) : (
											<Link to={'/ordersV2'}>
												<Button secondary>
													<GoToIcon />
													Show Orders
												</Button>
											</Link>
										)}
									</FlexRow>

									<FlexRow>
										<StatValue>
											{statsFetching ? (
												<CircularProgress
													size={16}
													showSpinner={statsFetching}
												/>
											) : (
												(stats && stats.orders && stats.orders.pending) || 0
											)}
										</StatValue>
										<StatText>Awaiting Fullfillment</StatText>
									</FlexRow>

									<FlexRow>
										<StatValue>
											{statsFetching ? (
												<CircularProgress
													size={16}
													showSpinner={statsFetching}
												/>
											) : (
												(stats && stats.orders && stats.orders.confirmed) || 0
											)}
										</StatValue>
										<StatText>In Last 7 Days</StatText>
									</FlexRow>
								</SummaryCardInner>
							</SummaryCardOuter>
						</FlexColumn>

						<FlexColumn style={{ flex: 1 }}>
							<SummaryCardOuter height='100%'>
								<SummaryCardInner>
									<FlexRow style={{ alignItems: 'center', justifyContent: 'space-between' }}>
										<SubTitle>Inventory</SubTitle>

										<Link to={'/inventoryV2'}>
											<Button secondary>
												<GoToIcon />
												Show Inventory
											</Button>
										</Link>
									</FlexRow>

									<FlexRow>
										<StatValue>
											{statsFetching ? (
												<CircularProgress
													size={16}
													showSpinner={statsFetching}
												/>
											) : (
												(stats && stats.inventory && stats.inventory.instock) || 0
											)}
										</StatValue>
										<StatText>In Stock</StatText>
									</FlexRow>

									<FlexRow>
										<StatValue>
											{statsFetching ? (
												<CircularProgress
													size={16}
													showSpinner={statsFetching}
												/>
											) : (
												(stats && stats.inventory && stats.inventory.total) || 0
											)}
										</StatValue>
										<StatText>Total Items</StatText>
									</FlexRow>
								</SummaryCardInner>
							</SummaryCardOuter>
						</FlexColumn>
					</FlexRow>
				</Container>

				<Container style={{ margin: '1rem 0 0 0' }}>
					<FlexRow style={{ padding: '0 1.5rem', gap: '1rem' }}>
						<SummaryCardOuter width='100%'>
							<SummaryCardInner>
								<FlexRow style={{ marginBottom: '2rem' }}>
									<SubTitle style={{ marginBottom: '1rem' }}>Sales Summary</SubTitle>

									<FlexColumn style={{ flex: '1' }}>
										<FlexRow style={{ justifyContent: 'flex-end' }}>
											<ButtonGroup alignRight>
												<FilterButton
													primary
													outline
													onClick={(e) => this.handlePopoverClick(PopoverIds.FILTER_BY_PERIOD, e)}
												>
													<ScheduleIcon />
													{filterByPeriodLabel}
													<ArrowDownIcon />
												</FilterButton>

												<LocalizationProvider>
													<DateRangePicker
														calendars={2}
														value={dateRange}
														open={dateRangePickerOpen}
														disableCloseOnSelect={false}
														disableFuture
														orientation={'landscape'}
														disableHighlightToday={true}
														onChange={(dateRange) => this.handleDateChange(dateRange)}
														onClose={(dateRange) => this.handleDateSelectClose(dateRange)}
														renderInput={(startProps, endProps) => (
															<FilterButton
																primary
																outline
																onClick={(e) => {
																	this.handleDateSelectOpen();
																}}
															>
																<CalendarIcon />
																{dateRange && dateRange[0] && dateRange[1]
																	? `${dateRange[0].toLocaleString('default', { month: 'short' })} ${dateRange[0].getDate()} - ${dateRange[1].toLocaleString(
																			'default',
																			{ month: 'short' }
																	  )} ${dateRange[1].getDate()}`
																	: 'Select Date'}
																<ArrowDownIcon />
															</FilterButton>
														)}
													/>
												</LocalizationProvider>
											</ButtonGroup>
										</FlexRow>
									</FlexColumn>
								</FlexRow>

								<BarChart
									data={graphData || []}
									unit={dateUnit}
									enableRedirect={disableOrdersV2}
								/>
							</SummaryCardInner>
						</SummaryCardOuter>
					</FlexRow>
				</Container>
			</RootContainer>
		);
	}
}

const mapStatesToProps = (state) => {
	let supplierSellerPortalConfig = state.supplier.sellerPortalConfig || {};
	let userSellerPortalConfig = state.session.sellerPortalConfig || {};

	let disableBulkUploadOrderTracking = supplierSellerPortalConfig.disable_bulk_upload_order_tracking || userSellerPortalConfig.disable_bulk_upload_order_tracking || false;

	return {
		disableBulkUploadOrderTracking,
		supplier: state.supplier.supplier,
		stats: state.stats.stats,
		graphData: state.stats.graph_stats || [],
		disableOrdersV2: state.session.disableOrdersV2 || false,
		uploadOrdersFileType: state.orders.uploadOrdersFileType || '',
		showUploadOrdersFile: (state.orders.showUploadOrdersFile && state.orders.uploadOrdersFileAction !== UploadOrdersFileActions.UPLOAD) || false,
	};
};

const mapDispatch = {
    changeTile,
    getStats,
    getSalesSummary,
    getDSOList,
    updateError,
	setUploadOrdersFileAction,
	setUploadOrdersFileBatch, 
	fetchUploadOrdersFileBatch
};

export default withRouter(connect(mapStatesToProps, mapDispatch)(Home));
